import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Config, I18n, I18nContext } from '@deporvillage/utils-library-component';
import { initialize as initializeTracking, track } from '@deporvillage/tracking-library-component';
import { AutocompleteWrapper, AutocompleteListClick } from '@deporvillage/autocomplete-component';
import CollapsibleList from '@deporvillage/collapsible-list-component';
import CountriesList from '@deporvillage/countries-list-component';
import { MyFavourites } from '@deporvillage/header-actions-component';
import CartDisplay, {
    CartDisplayClick,
    CartDisplayScroll,
} from '@deporvillage/cart-display-component';
import Picture from '@deporvillage/picture-component';
import Sidebar, { SidebarPosition } from '@deporvillage/sidebar-component';
import { LoginPopupExtended } from '@deporvillage/login-popup-component';
import HeaderLogo from './HeaderLogo';
import TopHeader from './TopHeader';

import styles from './HeaderMobile.module.scss';

const THRESHOLD_SCROLL_UP = 10;
const THRESHOLD_TOP = 138;

const PageType = {
    Home: 'home',
};

const HeaderCategoryLevel = {
    Category: 0,
    Subcategory: 1,
    Family: 2,
};

const HeaderCategoryClick = {
    Category: 'category',
    Subcategory: 'subcategory',
    Family: 'family',
};

const getHeaderCategories = (categories) =>
    categories.filter((i) => {
        if (i.mobile && i.items) i.items = getHeaderCategories(i.items);
        return i.mobile;
    });

const clone = (c) =>
    c.reduce((p, v) => {
        p.push({ ...v, ...(v.items ? { items: clone(v.items) } : {}) });
        return p;
    }, []);

const HeaderMobile = ({ model }) => {
    const { showFinance, showBestPrice, nav, stores, items, amount, totalItems, isOpened } = model;

    const { lang, page, url, tracking, debug } = React.useContext(Config);
    const { translate } = React.useContext(I18nContext);

    const langElem = React.useRef(null);

    const [user, setUser] = React.useState({});
    const [showLangMenu, setShowLangMenu] = React.useState(false);
    const [toggleSidebar, setToggleSidebar] = React.useState(false);
    const [prevScroll, setPrevScroll] = React.useState(0);
    const [isVisible, setIsVisible] = React.useState(true);
    const [isTopHeaderVisible, setTopHeaderVisible] = React.useState(false);
    const [showLoginPopup, setShowLoginPopup] = React.useState(false);

    // TODO: temp fix exclude new domains
    const langs = stores.filter((s) => s.lang !== lang && ['nl', 'da', 'pl'].indexOf(s.lang) === -1);

    const headerCategories = React.useMemo(() => {
        return getHeaderCategories(clone(nav));
    }, [nav]);

    const handleToggleSidebar = () => {
        !toggleSidebar &&
            track({
                type: 'ACTION',
                name: 'GENERIC_CLICK',
                action: 'Ir a Menu Header',
                label: 'Click en icono Menu',
            });
        setToggleSidebar(!toggleSidebar);
    };

    const handleLangMenuClick = () => setShowLangMenu(!showLangMenu);

    const handleUserData = ({ detail }) => {
        setUser({
            userName: detail.name,
            isLogged: detail.logged_in,
            sessionId: detail.session_id,
            favorites: detail.favorites,
        });

        /* eslint-disable-next-line no-undef */
        window.removeEventListener('user:data', handleUserData);
    };

    const handleWishListUpdate = React.useCallback(
        (obj) => {
            const favorites = (user.favorites || 0) + (obj.detail.isSaved ? 1 : -1);
            setUser({ ...user, favorites: favorites < 0 ? 0 : favorites });
        },
        [user],
    );

    /* eslint-disable-next-line no-undef */
    const redirect = (path) => (window.location.href = `${url}${path}`);

    const handleCategoryClick = (url, level) => {
        switch (level) {
            case HeaderCategoryLevel.Subcategory:
                track({
                    type: 'ACTION',
                    name: 'GENERIC_CLICK',
                    action: 'Ir a Menu Header',
                    label: 'Click en link Subcategoria Menu header',
                });
                break;
            case HeaderCategoryLevel.Family:
                track({
                    type: 'ACTION',
                    name: 'GENERIC_CLICK',
                    action: 'Ir a Menu Header',
                    label: 'Click en link Familia Menu header',
                });
                break;
            case HeaderCategoryLevel.Category:
            default:
                track({
                    type: 'ACTION',
                    name: 'GENERIC_CLICK',
                    action: 'Ir a Menu Header',
                    label: 'Click en link Categoria Menu header',
                });
                break;
        }

        /* eslint-disable-next-line no-undef */
        window.location.href = url;
    };

    /* istanbul ignore next */
    const handleOnSubmitAutocomplete = () =>
        track({
            type: 'ACTION',
            name: 'GENERIC_CLICK',
            action: 'Ir a Busqueda',
            label: 'Click en lanzar Busqueda',
        });

    /* istanbul ignore next */
    const handleOnClickAutocomplete = (e) => {
        switch (e) {
            case AutocompleteListClick.Brand:
                track({
                    type: 'ACTION',
                    name: 'GENERIC_CLICK',
                    action: 'Ir a Busqueda',
                    label: 'Click en marca',
                });
                break;
            case AutocompleteListClick.Category:
                track({
                    type: 'ACTION',
                    name: 'GENERIC_CLICK',
                    action: 'Ir a Busqueda',
                    label: 'Click en categoria',
                });
                break;
            case AutocompleteListClick.All:
                track({
                    type: 'ACTION',
                    name: 'GENERIC_CLICK',
                    action: 'Ir a Busqueda',
                    label: 'Click en ver todo',
                });
                break;
            case AutocompleteListClick.Close:
                track({
                    type: 'ACTION',
                    name: 'GENERIC_CLICK',
                    action: 'Ir a Busqueda',
                    label: 'Click en cruz de cierre',
                });
                break;
            case AutocompleteListClick.Item:
                track({
                    type: 'ACTION',
                    name: 'GENERIC_CLICK',
                    action: 'Ir a Busqueda',
                    label: 'Click en listado Busqueda',
                });
                break;
        }
    };

    /* istanbul ignore next */
    const handleOnClickHeaderActions = (e) => {
        /* istanbul ignore next */
        switch (e) {
            case CartDisplayClick.Item:
                track({
                    type: 'ACTION',
                    name: 'GENERIC_CLICK',
                    action: 'Ver Carrito',
                    label: 'Click en link Producto carrito',
                });
                break;
            case CartDisplayClick.Icon:
                track({
                    type: 'ACTION',
                    name: 'GENERIC_CLICK',
                    action: 'Ver Carrito',
                    label: 'Click en Icono carrito',
                });
                break;
            case CartDisplayClick.ViewCart:
                track({
                    type: 'ACTION',
                    name: 'GENERIC_CLICK',
                    action: 'Ver Carrito',
                    label: 'Click en boton Ver carrito',
                });
                /* eslint-disable-next-line no-undef */
                window.location.href = '/checkout/cart';
                break;
            case CartDisplayClick.PlaceOrder:
                track({
                    type: 'ACTION',
                    name: 'GENERIC_CLICK',
                    action: 'Ver Carrito',
                    label: 'Click en boton Realizar pedido',
                });
                /* eslint-disable-next-line no-undef */
                window.location.href = '/onestepcheckout';
                break;
            case CartDisplayClick.Component:
                /* eslint-disable-next-line no-undef */
                window.dispatchEvent(new CustomEvent('cart:component-click'));
                break;
        }
    };

    /* istanbul ignore next */
    const handleOnScrollHeaderActions = (e) => {
        /* istanbul ignore next */
        switch (e) {
            case CartDisplayScroll.Component:
                /* eslint-disable-next-line no-undef */
                window.dispatchEvent(new CustomEvent('cart:component-scroll'));
                break;
        }
    };

    /* istanbul ignore next */
    const handleOnClickUserIcon = () => {
        if (!user.isLogged) {
            setShowLoginPopup(true);
        } else {
            return redirect('/customer/account');
        }
    };

    React.useEffect(() => {
        /* eslint-disable no-undef */
        window.addEventListener('wishlist:update', handleWishListUpdate);
        return () => {
            window.removeEventListener('wishlist:update', handleWishListUpdate);
        };
        /* eslint-enable no-undef */
    }, [handleWishListUpdate]);

    React.useEffect(() => {
        showLangMenu &&
            langElem.current.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
    }, [showLangMenu]);

    React.useEffect(() => {
        initializeTracking({ ...tracking, page, debug });

        /* eslint-disable no-undef */
        window.addEventListener('user:data', handleUserData);
        window.dispatchEvent(new CustomEvent('user:getdata'));
        return () => {
            window.removeEventListener('user:data', handleUserData);
        };
        /* eslint-enable no-undef */
    }, []);

    // istanbul ignore next
    const handleScroll = () => {
        /* eslint-disable-next-line no-undef */
        const currentPos = window.pageYOffset;
        const isScrollUp = prevScroll > currentPos;
        const isScrollDw = currentPos < THRESHOLD_TOP;
        const diff = prevScroll - currentPos > THRESHOLD_SCROLL_UP;
        setIsVisible(
            !prevScroll ||
            (isVisible && isScrollUp) ||
            (!isVisible && isScrollUp && diff) ||
            isScrollDw,
        );
        setPrevScroll(currentPos);
    };

    React.useEffect(() => {
        /* eslint-disable no-undef */
        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
        /* eslint-enable no-undef */
    }, [prevScroll, isVisible, handleScroll]);

    return (
        <div className={styles['header-mobile-component']}>
            <header>
                <div
                    className={`${styles['top-header-wrapper']} ${
                        /* istanbul ignore next */ !isTopHeaderVisible && !isVisible
                            ? styles['is-not-visible']
                            : ''
                        }`}
                >
                    <TopHeader showFinance={showFinance} showBestPrice={showBestPrice} />
                </div>

                <div className={styles['header-top-bar']}>
                    <button
                        className={styles['header-toggle-menu']}
                        data-testid={'mobile-btn-open-sidebar'}
                        type='button'
                        onClick={handleToggleSidebar}
                    >
                        <span className={`${styles['icon']} icon icon-menu`} />
                    </button>

                    <div className={styles['header-logo']}>
                        <HeaderLogo
                            size={{ width: 136, height: 64 }}
                            wrapper={(children) =>
                                PageType.Home === page ? <h1>{children}</h1> : children
                            }
                        />
                    </div>

                    <div className={styles['header-actions']}>
                        <div className={`${styles['user-icon']}`} onClick={handleOnClickUserIcon}>
                            <span className={'icon icon-user'} />
                        </div>
                        {showLoginPopup ? (
                            <LoginPopupExtended onClose={() => setShowLoginPopup(false)} />
                        ) : null}
                        <MyFavourites logged={user.isLogged} favorites={user.favorites} />
                        <CartDisplay
                            items={items}
                            amount={amount}
                            totalItems={totalItems}
                            onClick={handleOnClickHeaderActions}
                            onScroll={handleOnScrollHeaderActions}
                            isOpened={isOpened}
                        />
                    </div>
                </div>

                <div className={styles['autocomplete-wrapper']}>
                    <AutocompleteWrapper
                        onSubmit={handleOnSubmitAutocomplete}
                        onClick={handleOnClickAutocomplete}
                        onTopHeaderVisible={setTopHeaderVisible}
                    />
                </div>
            </header>

            <Sidebar
                open={toggleSidebar}
                onClose={handleToggleSidebar}
                position={SidebarPosition.Left}
            >
                <div className={styles['header-mobile-sidebar']}>
                    <nav className={styles['header-categories']}>
                        {headerCategories.map((n) => (
                            <CollapsibleList key={n.url} {...n} onClick={handleCategoryClick} />
                        ))}
                    </nav>

                    <div className={styles['user-menu']}>
                        <span>
                            <span className={`${styles['icon']} icon icon-user`} />{' '}
                            <I18n>header.actions.sidebar_title_section</I18n>
                        </span>

                        {user.isLogged ? (
                            <Fragment>
                                <a href={'/customer/account'}>
                                    <I18n>header.actions.my_account</I18n>
                                </a>
                                <a href={translate('header.actions.help_url')} rel='nofollow'>
                                    <I18n>header.actions.help</I18n>
                                </a>
                                <a href={translate('header.actions.contact_url')}>
                                    <I18n>header.actions.contact</I18n>
                                </a>
                                <a href={'/checkout/cart'}>
                                    <I18n>header.actions.my_cart</I18n>
                                </a>
                                <a href={'/customer/account/logout'}>
                                    <I18n>header.actions.logout</I18n>
                                </a>
                            </Fragment>
                        ) : (
                            <Fragment>
                                <span
                                    onClick={() =>
                                        redirect(
                                            encodeURI(
                                                `/customer/login`,
                                            ),
                                        )
                                    }
                                >
                                    <I18n>header.actions.my_account</I18n>
                                </span>
                                <span
                                    onClick={() =>
                                        redirect(
                                            encodeURI(
                                                `/customer/register?referer=${window.location.href}`,
                                            ),
                                        )
                                    }
                                    rel='nofollow'
                                >
                                    <I18n>header.actions.sign_up</I18n>
                                </span>
                                <a href={translate('header.actions.help_url')} rel='nofollow'>
                                    <I18n>header.actions.help</I18n>
                                </a>
                                <a href={translate('header.actions.contact_url')}>
                                    <I18n>header.actions.contact</I18n>
                                </a>
                                <span onClick={() => redirect('/checkout/cart')}>
                                    <I18n>header.actions.my_cart</I18n>
                                </span>
                            </Fragment>
                        )}
                    </div>

                    <div className={styles['countries-list']}>
                        <CountriesList />
                    </div>

                    <div
                        className={`${styles['lang-menu']} ${showLangMenu ? styles['is-open'] : ''
                            }`}
                        ref={langElem}
                    >
                        <span
                            className={styles['lang-menu-toggle']}
                            onClick={handleLangMenuClick}
                            data-testid={'mobile-lang-menu-toggle'}
                        >
                            <span>
                                <I18n>header.langs.title</I18n>
                                <span>
                                    <I18n>header.langs.name</I18n>
                                </span>
                            </span>
                        </span>

                        {
                            langs.map((s) => (
                                <span
                                    className={styles['lang-menu-option']}
                                    key={s.lang}
                                    onClick={
                                        /* eslint-disable-next-line no-undef */
                                        () => (window.location.href = s.url)
                                    }
                                >
                                    {showLangMenu ? (
                                        <Picture
                                            className={styles['flag-icon']}
                                            src={`static/flags/${s.flag.toLowerCase()}.png`}
                                            width={16}
                                            height={12}
                                            name={`${s.flag} - ${s.name}`}
                                        />
                                    ) : null}
                                    {s.name}
                                </span>
                            ))
                        }
                    </div>
                </div>
            </Sidebar>
        </div>
    );
};

HeaderMobile.propTypes = {
    model: PropTypes.object,
    onClick: PropTypes.func,
};

export default HeaderMobile;
export { HeaderCategoryClick };
